import React, { useMemo, useState, useEffect } from 'react';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { Edit3, Filter, Search, Trash2, DownloadCloud } from 'lucide-react';
import { FIELD_DEFINITIONS, useAuth } from '../../context/AuthContext';
import './StudentPage.css';
import Spinner from '../Common/Spinner';
import { showConfirmDialog, showToast } from '../../utils/feedback';

const STANDARD_OPTIONS = ['Nursery', 'LKG', 'UKG', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII', 'DEGREE', 'PG', 'OTHER'];
const DEFAULT_DIVISIONS = ['A', 'B', 'C', 'D', 'E', 'F'];

const StudentTable = ({ students = [], loading = false, onDelete, onEdit, onFilteredStudentsChange }) => {
  const { fieldPermissions } = useAuth();
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [standardFilter, setStandardFilter] = useState('');
  const [divisionFilter, setDivisionFilter] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [updateFilter, setUpdateFilter] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);

  // 1. ഈ രണ്ട് ഫംഗ്ഷനുകളും മുകളിലേക്ക് മാറ്റി (Error ഒഴിവാക്കാൻ)
  const isStudentUpdated = (student) => {
    return Boolean(
      student?.isUpdated ||
      student?.is_updated ||
      student?.updated ||
      student?.updated_at ||
      student?.lastUpdated ||
      student?.update_type
    );
  };

  const formatUpdatedText = (student) => {
    const updatedValue = student?.updated_at || student?.lastUpdated || null;
    
    let badgeText = 'Updated';
    if (student.update_type === 'photo') badgeText = 'Photo Updated';
    else if (student.update_type === 'data') badgeText = 'Data Updated';
    else if (student.update_type === 'both') badgeText = 'Fully Updated';

    if (!updatedValue) return badgeText;

    try {
      let dateObj = null;
      if (typeof updatedValue?.toDate === 'function') {
        dateObj = updatedValue.toDate(); 
      } else {
        const parsed = new Date(updatedValue);
        if (!Number.isNaN(parsed.getTime())) dateObj = parsed;
      }

      if (dateObj) {
        const day = String(dateObj.getDate()).padStart(2, '0');
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const year = dateObj.getFullYear();
        return `${badgeText} ${day}/${month}/${year}`;
      }
    } catch (err) {
      return badgeText;
    }
    return badgeText;
  };

  const visibleFields = useMemo(
    () => FIELD_DEFINITIONS.filter((field) => ['student_name', 'std_roman'].includes(field.key) || fieldPermissions?.[field.key]),
    [fieldPermissions]
  );

  const divisionOptions = useMemo(() => {
    const customValues = students.map((s) => (s.custom_div || s.division || '').trim()).filter(Boolean);
    return [...new Set([...DEFAULT_DIVISIONS, ...customValues])];
  }, [students]);

  const searchTerms = useMemo(
    () => searchTerm
      .split(',')
      .map((term) => term.trim().toLowerCase())
      .filter(Boolean),
    [searchTerm]
  );

  const filteredStudents = useMemo(() => {
    return students.filter((s) => {
      const studentName = (s.student_name || '').toLowerCase();
      const matchesSearch =
        searchTerms.length === 0 ||
        searchTerms.some((term) => studentName.includes(term));
      const matchesStandard = !standardFilter || (s.std_roman || '').trim() === standardFilter;
      const studentDivision = (s.custom_div || s.division || '').trim();
      const matchesDivision = !divisionFilter || studentDivision === divisionFilter;
      
      const matchesUpdate = !updateFilter || s.update_type === updateFilter || (updateFilter === 'updated' && isStudentUpdated(s));

      return matchesSearch && matchesStandard && matchesDivision && matchesUpdate;
    });
  }, [students, searchTerms, standardFilter, divisionFilter, updateFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, standardFilter, divisionFilter, rowsPerPage, updateFilter]);

  useEffect(() => {
    onFilteredStudentsChange?.({
      students: filteredStudents,
      filters: { searchTerm, standardFilter, divisionFilter, updateFilter },
    });
  }, [filteredStudents, searchTerm, standardFilter, divisionFilter, updateFilter, onFilteredStudentsChange]);

  const totalPages = Math.max(1, Math.ceil(filteredStudents.length / rowsPerPage));

  const paginatedStudents = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    return filteredStudents.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredStudents, currentPage, rowsPerPage]);

  const handleDelete = async (id) => {
    const res = await showConfirmDialog({
      title: 'Delete this student?',
      text: 'This action cannot be undone and will remove the linked student record.',
      icon: 'warning',
      confirmButtonText: 'Delete Student',
      cancelButtonText: 'Keep Record',
      confirmButtonColor: '#dc2626',
    });

    if (!res.isConfirmed) return;

    try {
      setDeletingId(id);
      await onDelete(id);
      showToast({ icon: 'success', title: 'Student removed successfully.' });
    } catch (e) {
      showToast({ icon: 'error', title: e?.message || 'Delete failed.' });
    } finally {
      setDeletingId(null);
    }
  };

  const handleBulkDownloadPhotos = async () => {
    const studentsWithPhotos = filteredStudents.filter(s => s.photo_url);

    if (studentsWithPhotos.length === 0) {
      showToast({ icon: 'warning', title: 'No photos found in the filtered list.' });
      return;
    }

    setIsDownloading(true);
    const zip = new JSZip();
    let downloadedCount = 0;

    try {
      const fetchPromises = studentsWithPhotos.map(async (student) => {
        try {
          const response = await fetch(student.photo_url);
          const blob = await response.blob();
          
          let fileName = student.photo_name || student.originalPhotoName;
          if (!fileName) {
            fileName = `${student.student_name.replace(/[^a-zA-Z0-9]/g, '_')}.jpg`;
          }

          zip.file(fileName, blob);
          downloadedCount++;
        } catch (err) {
          console.error(`Failed to fetch photo for ${student.student_name}:`, err);
        }
      });

      await Promise.all(fetchPromises);

      if (downloadedCount > 0) {
        const content = await zip.generateAsync({ type: 'blob' });
        saveAs(content, `Student_Photos_${new Date().toISOString().slice(0,10)}.zip`);
        showToast({ icon: 'success', title: `Successfully downloaded ${downloadedCount} photos!` });
      } else {
        showToast({ icon: 'error', title: 'Failed to download photos.' });
      }
    } catch (error) {
      console.error("Zip generation error:", error);
      showToast({ icon: 'error', title: 'Error generating zip file.' });
    } finally {
      setIsDownloading(false);
    }
  };

  const hasPhotoColumn = visibleFields.some((f) => f.key === 'photo_url');
  
  // പുതിയ Sl No കോളം കൂടി ചേർത്തതിനാൽ colSpan വാല്യൂ +1 വർദ്ധിപ്പിച്ചു
  const colSpan = visibleFields.filter((f) => f.key !== 'photo_name').length + 2;

  return (
    <div className="app-panel table-panel">
      <div className="table-toolbar d-flex flex-wrap align-items-center gap-3">
        <div>
          <div className="table-title">Student Records</div>
          <div className="table-subtitle">Total Students: <strong>{filteredStudents.length}</strong></div>
        </div>

        <button
          className="btn btn-primary btn-sm rounded-pill d-flex align-items-center gap-2 px-3"
          onClick={handleBulkDownloadPhotos}
          disabled={isDownloading || filteredStudents.length === 0}
          style={{ marginLeft: 'auto' }}
        >
          {isDownloading ? (
            <>
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
              Zipping...
            </>
          ) : (
            <>
              <DownloadCloud size={16} />
              Download Photos
            </>
          )}
        </button>

        <div className="table-filters">
          <div className="app-filter-input">
            <Search size={16} />
            <input
              type="text"
              placeholder="Search students by name (comma separated)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="app-select-wrap">
            <Filter size={16} />
            <select className="form-select form-select-sm app-select" value={standardFilter} onChange={(e) => setStandardFilter(e.target.value)}>
              <option value="">All Standards</option>
              {STANDARD_OPTIONS.map((value) => <option key={value} value={value}>{value}</option>)}
            </select>
          </div>

          <select className="form-select form-select-sm app-select" value={divisionFilter} onChange={(e) => setDivisionFilter(e.target.value)}>
            <option value="">All Divisions</option>
            {divisionOptions.map((value) => <option key={value} value={value}>{value}</option>)}
          </select>

          <select className="form-select form-select-sm app-select" value={updateFilter} onChange={(e) => setUpdateFilter(e.target.value)}>
            <option value="">All Status</option>
            <option value="updated">Any Updates</option>
            <option value="data">Data Updated</option>
            <option value="photo">Photo Updated</option>
            <option value="both">Fully Updated</option>
          </select>

          <select className="form-select form-select-sm app-select rows-select" value={rowsPerPage} onChange={(e) => setRowsPerPage(Number(e.target.value))}>
            {[10, 25, 50, 100].map((count) => <option key={count} value={count}>{count} rows</option>)}
          </select>
        </div>
      </div>

      <div className="table-responsive">
        <table className="table align-middle app-table mb-0">
          <thead>
            <tr>
              {/* 1. ആദ്യത്തെ കോളമായി Sl No ഹെഡർ ചേർത്തു */}
              <th style={{ width: 70 }}>Sl No</th>
              {hasPhotoColumn && <th>Photo</th>}
              {visibleFields
                .filter((f) => f.key !== 'photo_url' && f.key !== 'photo_name')
                .map((field) => <th key={field.key}>{field.label}</th>)}
              <th style={{ width: 180 }}>Action</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={colSpan} className="text-center py-5">
                  <Spinner label="Loading student records..." />
                </td>
              </tr>
            ) : filteredStudents.length === 0 ? (
              <tr>
                <td colSpan={colSpan} className="text-center py-5 text-muted">No matching students found.</td>
              </tr>
            ) : (
              // 2. map ഫംഗ്ഷനിൽ index പാരാമീറ്റർ ഉൾപ്പെടുത്തി
              paginatedStudents.map((s, index) => {
                const updated = isStudentUpdated(s);
                
                // 3. പേജ് കണക്കാക്കി സീരിയൽ നമ്പർ ഉണ്ടാക്കുന്നു
                const slNo = (currentPage - 1) * rowsPerPage + index + 1;

                return (
                  <tr
                    key={s.id}
                    className={updated ? 'student-row-updated' : ''}
                  >
                    {/* 4. ആദ്യ സെല്ലിൽ സീരിയൽ നമ്പർ നൽകുന്നു */}
                    <td className="text-muted fw-semibold">{slNo}</td>

                    {hasPhotoColumn && (
                      <td>
                        <div className="student-photo-hover-wrap">
                          <img
                            src={s.photo_url || 'https://placehold.co/60'}
                            width="100"
                            height="100"
                            className="student-thumb student-thumb-hoverable"
                            alt={s.student_name || 'student'}
                          />
                          <div className="student-photo-popover">
                            <img
                              src={s.photo_url || 'https://placehold.co/200'}
                              className="student-photo-preview-large"
                              alt={s.student_name || 'student preview'}
                            />
                          </div>
                        </div>
                      </td>
                    )}

                    {visibleFields
                      .filter((f) => f.key !== 'photo_url' && f.key !== 'photo_name')
                      .map((field) => {
                        if (field.key === 'student_name') {
                          return (
                            <td key={field.key}>
                              <div className="d-flex align-items-center gap-2 flex-wrap">
                                <span>{s[field.key] || '-'}</span>
                                {updated && (
                                  <span className="student-updated-badge">
                                    {formatUpdatedText(s)}
                                  </span>
                                )}
                              </div>
                            </td>
                          );
                        }

                        return <td key={field.key}>{s[field.key] || '-'}</td>;
                      })}

                    <td>
                      <div className="d-flex flex-wrap gap-2">
                        {onEdit && (
                          <button className="btn btn-outline-primary btn-sm rounded-pill px-3" onClick={() => onEdit(s)}>
                            <Edit3 size={14} className="me-1" /> Edit
                          </button>
                        )}
                        <button className="btn btn-outline-danger btn-sm rounded-pill px-3" onClick={() => handleDelete(s.id)} disabled={deletingId === s.id}>
                          {deletingId === s.id ? (
                            <span className="d-inline-flex align-items-center gap-2">
                              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                              Deleting...
                            </span>
                          ) : (
                            <>
                              <Trash2 size={14} className="me-1" /> Delete
                            </>
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {!loading && filteredStudents.length > 0 && (
        <div className="table-footer">
          <div className="small text-muted">
            Showing {(currentPage - 1) * rowsPerPage + 1} to {Math.min(currentPage * rowsPerPage, filteredStudents.length)} of {filteredStudents.length}
          </div>

          <div className="d-flex align-items-center gap-2 flex-wrap">
            <button className="btn btn-sm btn-outline-secondary rounded-pill px-3" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>First</button>
            <button className="btn btn-sm btn-outline-secondary rounded-pill px-3" onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))} disabled={currentPage === 1}>Previous</button>
            <span className="page-chip">Page {currentPage} of {totalPages}</span>
            <button className="btn btn-sm btn-outline-secondary rounded-pill px-3" onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages}>Next</button>
            <button className="btn btn-sm btn-outline-secondary rounded-pill px-3" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>Last</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentTable;